import {
  Bell,
  CalendarClock,
  CalendarDays,
  Clock,
  Gauge,
  Send,
  ShieldCheck,
  Timer,
  Zap,
} from 'lucide-react';
import {
  AvisosDeAjustes,
  BannerDeActivacion,
  BarraDeGuardado,
  Campo,
  CargandoAjustes,
  FalloDeAjustes,
  FilaConInterruptor,
  Seccion,
  entrada,
  useAjustes,
} from './ajustes';

export function PaginaEmision() {
  const ajustes = useAjustes(['emision', 'cobranza', 'sri']);

  if (ajustes.cargando) {
    return <CargandoAjustes bloques={4} />;
  }
  if (ajustes.fallo) {
    return <FalloDeAjustes causa={ajustes.fallo} />;
  }

  const emisionActiva = ajustes.booleano('emision_activa');
  const envioAutomatico = ajustes.booleano('sri_procesamiento_automatico');
  const notificaAdmin = ajustes.booleano('emision_notificar_admin');

  const dia = ajustes.texto('emision_dia');
  const hora = ajustes.texto('emision_hora');

  return (
    <div className="grid gap-6">
      <AvisosDeAjustes ajustes={ajustes} />

      <BannerDeActivacion
        icono={Zap}
        activo={emisionActiva}
        titulo={emisionActiva ? 'Emisión automática activada' : 'Emisión automática desactivada'}
        descripcion={
          emisionActiva
            ? `${proximaEmision(dia)} a las ${hora || '—'}`
            : 'Actívela para generar los cobros del mes sin que nadie tenga que lanzarlos'
        }
        alCambiar={() => ajustes.alternar('emision_activa', emisionActiva)}
      />

      <Seccion icono={CalendarClock} color="azul" titulo="Programación de la emisión">
        <div className="grid gap-6 sm:grid-cols-3">
          <Campo
            etiqueta="Día del mes"
            icono={CalendarDays}
            ayuda="Del 1 al 28: los meses cortos no tienen 29, 30 ni 31"
          >
            <input
              type="number"
              min={1}
              max={28}
              value={dia}
              onChange={(e) => ajustes.editar('emision_dia', e.target.value)}
              className={`${entrada} importe pl-10`}
            />
          </Campo>

          <Campo etiqueta="Hora de ejecución" icono={Clock} ayuda="Fuera del horario de atención">
            <input
              type="time"
              value={hora}
              onChange={(e) => ajustes.editar('emision_hora', e.target.value)}
              className={`${entrada} pl-10`}
            />
          </Campo>

          <Campo
            etiqueta="Plazo de pago"
            icono={Timer}
            ayuda="Días desde la emisión hasta el vencimiento"
          >
            <input
              type="number"
              min={0}
              value={ajustes.texto('emision_plazo_pago_dias')}
              onChange={(e) => ajustes.editar('emision_plazo_pago_dias', e.target.value)}
              className={`${entrada} importe pl-10`}
            />
          </Campo>
        </div>
      </Seccion>

      <Seccion icono={ShieldCheck} color="violeta" titulo="Antes y después de emitir">
        <div className="grid gap-4">
          <FilaConInterruptor
            icono={ShieldCheck}
            color="ambar"
            titulo="Requiere aprobación manual"
            descripcion="Un administrador revisa antes de que se generen los cobros"
            activo={ajustes.booleano('emision_requiere_aprobacion')}
            alCambiar={() =>
              ajustes.alternar(
                'emision_requiere_aprobacion',
                ajustes.booleano('emision_requiere_aprobacion'),
              )
            }
          />

          <FilaConInterruptor
            icono={Bell}
            color="azul"
            titulo="Avisar al terminar"
            descripcion="Envía por correo el resumen de lo emitido"
            activo={notificaAdmin}
            alCambiar={() => ajustes.alternar('emision_notificar_admin', notificaAdmin)}
          >
            <Campo etiqueta="Correos que reciben el resumen" ayuda="Separados por coma">
              <input
                value={ajustes.texto('emision_notificar_correos')}
                onChange={(e) => ajustes.editar('emision_notificar_correos', e.target.value)}
                placeholder="juan.salgado@epagal.gob.ec, veronica.alulema@epagal.gob.ec"
                className={entrada}
              />
            </Campo>
          </FilaConInterruptor>
        </div>
      </Seccion>

      <Seccion icono={CalendarClock} color="marca" titulo="Políticas de cobro">
        <div className="grid gap-4">
          <FilaConInterruptor
            icono={CalendarClock}
            color="marca"
            titulo="Bloquear el cobro de un periodo no emitido"
            descripcion="Impide cobrar un mes cuyos comprobantes todavía no existen"
            activo={ajustes.booleano('cobro_bloquear_periodo_no_emitido')}
            alCambiar={() =>
              ajustes.alternar(
                'cobro_bloquear_periodo_no_emitido',
                ajustes.booleano('cobro_bloquear_periodo_no_emitido'),
              )
            }
          />

          <Campo
            etiqueta="Día desde el que se habilita el cobro"
            icono={CalendarDays}
            ayuda="Antes de ese día del mes la ventanilla no acepta el periodo corriente"
          >
            <input
              type="number"
              min={1}
              max={28}
              value={ajustes.texto('cobro_dia_habilitacion')}
              onChange={(e) => ajustes.editar('cobro_dia_habilitacion', e.target.value)}
              className={`${entrada} importe pl-10 sm:max-w-40`}
            />
          </Campo>
        </div>
      </Seccion>

      <BannerDeActivacion
        icono={Send}
        color="azul"
        activo={envioAutomatico}
        titulo={envioAutomatico ? 'Envío al SRI automático' : 'Envío al SRI manual'}
        descripcion={
          envioAutomatico
            ? 'Los comprobantes se transmiten solos después de cada emisión'
            : 'Los comprobantes se quedan esperando a que alguien los transmita'
        }
        alCambiar={() => ajustes.alternar('sri_procesamiento_automatico', envioAutomatico)}
      />

      <Seccion icono={Gauge} color="indigo" titulo="Transmisión al SRI">
        <div className="grid gap-4 sm:grid-cols-3">
          <Campo
            etiqueta="Comprobantes por lote"
            ayuda="Lotes grandes van más rápido y fallan enteros"
          >
            <input
              type="number"
              min={1}
              value={ajustes.texto('sri_lote_tamano')}
              onChange={(e) => ajustes.editar('sri_lote_tamano', e.target.value)}
              className={`${entrada} importe`}
            />
          </Campo>

          <Campo
            etiqueta="Espera entre comprobantes"
            ayuda="Milisegundos. El SRI limita las peticiones por minuto"
          >
            <input
              type="number"
              min={0}
              value={ajustes.texto('sri_espera_ms')}
              onChange={(e) => ajustes.editar('sri_espera_ms', e.target.value)}
              className={`${entrada} importe`}
            />
          </Campo>

          <Campo
            etiqueta="Reintentos automáticos"
            ayuda="Solo ante fallos de comunicación, nunca ante un rechazo"
          >
            <input
              type="number"
              min={0}
              value={ajustes.texto('sri_reintentos')}
              onChange={(e) => ajustes.editar('sri_reintentos', e.target.value)}
              className={`${entrada} importe`}
            />
          </Campo>
        </div>

        <div className="mt-4">
          <FilaConInterruptor
            icono={Bell}
            color="indigo"
            titulo="Avisar cuando termine el lote"
            descripcion="Envía por correo el resultado de la transmisión"
            activo={ajustes.booleano('sri_notificar_completado')}
            alCambiar={() =>
              ajustes.alternar(
                'sri_notificar_completado',
                ajustes.booleano('sri_notificar_completado'),
              )
            }
          />
        </div>
      </Seccion>

      <BarraDeGuardado ajustes={ajustes} />
    </div>
  );
}

function proximaEmision(dia: string): string {
  const numero = Number(dia);
  if (!Number.isInteger(numero) || numero < 1 || numero > 28) {
    return 'Indique un día del mes entre 1 y 28';
  }

  const hoy = new Date();
  const proxima = new Date(hoy.getFullYear(), hoy.getMonth(), numero);
  if (proxima <= hoy) {
    proxima.setMonth(proxima.getMonth() + 1);
  }

  const texto = proxima.toLocaleDateString('es-EC', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return `Próxima emisión: ${texto}`;
}
